import Button from "@mui/material/Button";
import { isArray } from "lodash";
import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import { useChecklistRouteHook } from "../../hooks/useChecklistRouteHook";
import { loanFetchedDataUwRequirementsSet } from "../../redux/reducers/loanFetchedDataReducer";
import { sidebarClose } from "../../redux/actions/sidebarActions";
import HtmlEditor from "../html/HtmlEditor";
import Flx from "../layout/Flx";

/**
 * Comment box for a single loan condition (underwriting requirement).
 *
 * Uses the rich TipTap editor (HtmlEditor) rather than a plain text input so
 * @-mentions work exactly like normal loan comments. The mention markup lives
 * inside the submitted `htmlString` — the backend derives the recipients from it
 * (Comment.at_mentioned_comment_to_email → html_mentions(htmlString)) and fires
 * the notifications, so nothing extra needs to be sent in the payload.
 */
const LoanConditionCommentInput = ({ loanId, conditionId, sx }) => {
  const { loading, addCommentToUnderwritingRequirement } =
    useChecklistRouteHook();
  const dispatch = useDispatch();

  const [comment, setComment] = useState(null);
  // Bumped after a successful submit to force-remount the uncontrolled editor
  // so its content clears (same trick LoanEnhancedCommentTextBox uses).
  const [editorKey, setEditorKey] = useState(0);

  const canSubmit = !!comment?.plainText?.trim();

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    addCommentToUnderwritingRequirement({
      dealId: loanId,
      requirementId: conditionId,
      commentText: comment?.htmlString,
      onSuccessFn: (updatedConditions) => {
        if (isArray(updatedConditions)) {
          dispatch(loanFetchedDataUwRequirementsSet(updatedConditions));
        } else {
          dispatch(loanFetchedDataUwRequirementsSet(null));
          dispatch(sidebarClose());
        }
        setComment(null);
        setEditorKey((k) => k + 1);
      },
    });
  }, [
    canSubmit,
    addCommentToUnderwritingRequirement,
    loanId,
    conditionId,
    comment,
    dispatch,
  ]);

  return (
    <Flx
      column
      fw
      gap={0}
      sx={{
        border: "1px solid #dde0e4",
        borderRadius: "6px",
        overflow: "hidden",
        background: "#ffffff",
        ...sx,
      }}
    >
      <HtmlEditor
        key={editorKey}
        value={comment}
        suppressOuterBorder
        onChange={setComment}
        placeholder="Add a comment… (type @ to mention a user)"
      />
      <Flx end sx={{ p: 1 }}>
        <Button
          size="small"
          loading={loading}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Flx>
    </Flx>
  );
};

export default LoanConditionCommentInput;
